import React, { useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import { DataTable, act, hint } from "../../Fin/gl/glUi";
import { EmptyState, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { ItemPicker, WarehouseSelect, fmtAmt, fmtQty, useInvSettings } from "./invUi";

// Inventory → Reorder: what to order now (min / max per warehouse, else the last months' usage and
// lost sales over the supplier's lead time), turned into draft purchase orders per preferred
// supplier; and the lost sales log (asked for, not served).

const REASONS = { NO_STOCK: "Not in stock", PRICE: "Price", LEAD_TIME: "Delivery time", OTHER: "Other" };

function Suggestions({ s }) {
  const [wh, setWh] = useState(null);
  const { data, loading, error, reload } = useLoad(() => stock.getReorder({ warehouse_id: wh || undefined }), [wh]);
  const [picked, setPicked] = useState({});
  const [qty, setQty] = useState({});
  const [urgent, setUrgent] = useState(false);
  const key = (r) => `${r.item_id}:${r.warehouse_id}`;
  const rows = data || [];
  const chosen = rows.filter((r) => picked[key(r)] && r.supplier_id);
  const create = async () => {
    const res = await act(() => stock.ordersFromReorder({ is_urgent: urgent, lines: chosen.map((r) => ({ item_id: r.item_id, warehouse_id: r.warehouse_id, supplier_id: r.supplier_id, quantity: Number(qty[key(r)] ?? r.suggest_purchase_qty), unit_cost: r.last_cost || 0 })) }));
    if (res) {
      setPicked({});
      reload();
    }
  };
  return (
    <>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <div style={{ minWidth: 260 }}>
          <WarehouseSelect id="ro-wh" all value={wh} onChange={setWh} />
        </div>
        <label className="d-flex align-items-center gap-1" style={{ fontSize: 14 }}>
          <input type="checkbox" checked={urgent} onChange={(e) => setUrgent(e.target.checked)} /> Urgent (VOR)
        </label>
        {s.can.purchase && s.purchasing_enabled ? (
          <PrimaryButton disabled={!chosen.length} onClick={create}>
            Create draft orders ({chosen.length})
          </PrimaryButton>
        ) : null}
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          rowKey={key}
          empty={<EmptyState title="Nothing to reorder" text="Set min / max per warehouse on the item, or let usage drive it." />}
          columns={[
            { label: "", render: (r) => <input type="checkbox" aria-label={`Order ${r.sku}`} disabled={!r.supplier_id} checked={!!picked[key(r)]} onChange={(e) => setPicked({ ...picked, [key(r)]: e.target.checked })} /> },
            { label: "Item", render: (r) => `${r.sku} ${r.item_name}` },
            { label: "Warehouse", render: (r) => r.warehouse_code },
            { label: "On hand", num: true, render: (r) => fmtQty(r.on_hand) },
            { label: "Reserved / back order", num: true, render: (r) => (r.reserved || r.back_ordered ? `${fmtQty(r.reserved)} / ${fmtQty(r.back_ordered)}` : "") },
            { label: "On order", num: true, render: (r) => (r.on_order ? fmtQty(r.on_order) : "") },
            { label: "Use / month", num: true, render: (r) => `${fmtQty(r.monthly_usage)}${r.lost_per_month ? ` (+${fmtQty(r.lost_per_month)} lost)` : ""}` },
            { label: "Reorder at / up to", num: true, render: (r) => `${fmtQty(r.reorder_point)} / ${fmtQty(r.target)}` },
            {
              label: "Order",
              render: (r) => (
                <span className="d-flex align-items-center gap-1">
                  <input type="number" min={0} step="any" className="form-control form-control-sm" style={{ width: 80 }} aria-label={`Quantity ${r.sku}`} value={qty[key(r)] ?? r.suggest_purchase_qty} onChange={(e) => setQty({ ...qty, [key(r)]: e.target.value })} />
                  <span style={hint}>{r.purchase_uom || r.unit_of_measure || ""}</span>
                </span>
              ),
            },
            { label: "Supplier", render: (r) => r.supplier_name || <span style={{ color: "#B91C1C", fontSize: 12 }}>No preferred supplier</span> },
            { label: "Last cost", num: true, render: (r) => fmtAmt(r.last_cost) },
          ]}
        />
      </LoadState>
    </>
  );
}

function RecordLostSale({ onHide, onDone }) {
  const [item, setItem] = useState(null);
  const [h, setH] = useState({ part_number_text: "", quantity: 1, reason: "NO_STOCK", customer_name: "", customer_phone: "", warehouse_id: null });
  const save = async () => {
    const res = await act(() => stock.recordLostSale({ ...h, item_id: item ? item.id : undefined, quantity: Number(h.quantity), source: "MANUAL" }));
    if (res) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title="Record a lost sale" width={560} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save}>Record</PrimaryButton></>}>
      <label className="form-label" htmlFor="ls-item">Item asked for</label>
      <ItemPicker id="ls-item" value={item ? item.id : null} onChange={setItem} />
      <input className="form-control mt-2" style={inputStyle} placeholder="…or the part number asked for (not in the list)" aria-label="Part number" value={h.part_number_text} onChange={(e) => setH({ ...h, part_number_text: e.target.value })} />
      <div className="d-flex flex-wrap gap-2 mt-2">
        <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 110 }} aria-label="Quantity" value={h.quantity} onChange={(e) => setH({ ...h, quantity: e.target.value })} />
        <select className="form-select" style={{ width: 200 }} aria-label="Reason" value={h.reason} onChange={(e) => setH({ ...h, reason: e.target.value })}>
          {Object.entries(REASONS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-2">
        <WarehouseSelect id="ls-wh" value={h.warehouse_id} onChange={(v) => setH({ ...h, warehouse_id: v })} placeholder="Warehouse (optional)" />
      </div>
      <div className="d-flex gap-2 mt-2">
        <input className="form-control" style={inputStyle} placeholder="Customer" aria-label="Customer" value={h.customer_name} onChange={(e) => setH({ ...h, customer_name: e.target.value })} />
        <input className="form-control" style={inputStyle} placeholder="Phone" aria-label="Phone" value={h.customer_phone} onChange={(e) => setH({ ...h, customer_phone: e.target.value })} />
      </div>
    </SlidePanel>
  );
}

function LostSales() {
  const { data, loading, error, reload } = useLoad(() => stock.listLostSales({}), []);
  const [adding, setAdding] = useState(false);
  return (
    <>
      <div className="mb-2">
        <PrimaryButton onClick={() => setAdding(true)}>Record a lost sale</PrimaryButton>
        <span style={{ ...hint, marginLeft: 8 }}>Lost sales count as demand in the reorder suggestion.</span>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          empty={<EmptyState title="No lost sales recorded" />}
          columns={[
            { label: "When", render: (r) => formatDate(r.recorded_at) },
            { label: "Item", render: (r) => (r.sku ? `${r.sku} ${r.item_name}` : r.part_number_text || r.description || "") },
            { label: "Qty", num: true, render: (r) => fmtQty(r.quantity) },
            { label: "Reason", render: (r) => REASONS[r.reason] || r.reason },
            { label: "From", render: (r) => `${r.source.toLowerCase().replace("_", " ")}${r.source_ref ? ` ${r.source_ref}` : ""}` },
            { label: "Customer", render: (r) => [r.customer_name, r.customer_phone].filter(Boolean).join(" · ") },
          ]}
        />
      </LoadState>
      {adding ? <RecordLostSale onHide={() => setAdding(false)} onDone={reload} /> : null}
    </>
  );
}

export default function Reorder() {
  const { data: s, loading, error, reload } = useInvSettings();
  const [tab, setTab] = useState("suggest");
  return (
    <div style={pageStyle}>
      <PageHeader title="Reorder" subtitle="What to order now, and demand that was not served" />
      <LoadState loading={loading} error={error} onRetry={reload}>
        {s ? (
          <>
            <Tabs tabs={[{ key: "suggest", label: "Suggested orders" }, { key: "lost", label: "Lost sales" }]} active={tab} onChange={setTab} />
            {tab === "suggest" ? <Suggestions s={s} /> : <LostSales />}
          </>
        ) : null}
      </LoadState>
    </div>
  );
}
